import { useState } from "react";
import { useAuth } from "@/context/auth-context";
import { useNavigate, Link } from "@tanstack/react-router";
import { useSnackbar } from "notistack";
import Button from "@/components/Button";
import Input from "@/components/Input";
import DatePicker from "@/components/DatePicker";
import { signUpSchema, type SignUpFormData } from "@/schemas/auth";
import { z } from "zod";

import signup from "../assets/imgs/signin.png";

const SignUpPage = () => {
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [birthdate, setBirthdate] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [gender, setGender] = useState<'male' | 'female' | ''>("");
  const [validationErrors, setValidationErrors] = useState<{
    name?: string;
    username?: string;
    birthdate?: string;
    email?: string;
    password?: string;
    passwordConfirm?: string;
    gender?: string;
  }>({});

  const { signUp, isSigningUp, signInWithGoogle, isSigningInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
      enqueueSnackbar("Successfully signed in with Google!", { variant: "success" });
      navigate({ to: "/dashboard" });
    } catch (err: any) {
      const errorMessage = err.message || "Google sign in failed";
      enqueueSnackbar(errorMessage, { variant: "error" });
    }
  };

  const validateForm = () => {
    try {
      const formData: SignUpFormData = {
        name,
        username,
        birthdate,
        email,
        password,
        passwordConfirm,
        gender: gender as 'male' | 'female',
      };
      signUpSchema.parse(formData);
      setValidationErrors({});
      return true;
    } catch (err) {
      if (err instanceof z.ZodError) {
        const errors: typeof validationErrors = {};
        err.errors.forEach((error) => {
          if (error.path[0]) {
            errors[error.path[0] as keyof typeof errors] = error.message;
          }
        });
        setValidationErrors(errors);
      }
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Format birthdate to ISO string with time set to 00:00:00
      const formattedBirthdate = birthdate
        ? new Date(birthdate + "T00:00:00.000Z").toISOString()
        : undefined;
      await signUp(
        email,
        password,
        passwordConfirm,
        name,
        username,
        formattedBirthdate,
        gender as 'male' | 'female'
      );
      enqueueSnackbar("Account created successfully! Welcome to Booq.", {
        variant: "success",
      });
      navigate({ to: "/dashboard" });
    } catch (err: any) {
      console.error("Sign up error:", err);
      console.error("Error data:", err.data);
      console.error("Full error object:", JSON.stringify(err, null, 2));

      // Handle PocketBase specific errors
      if (err.data && typeof err.data === "object") {
        const fieldErrors: typeof validationErrors = {};

        Object.keys(err.data).forEach((field) => {
          const fieldError = err.data[field];
          if (fieldError?.message) {
            // Map generic messages to user-friendly ones
            let message = fieldError.message;

            if (message.toLowerCase().includes("must be unique")) {
              if (field === "email") {
                message = "This email is already registered. Try signing in or use 'Forgot Password'.";
              } else if (field === "username") {
                message = "This username is taken. Please choose a different one.";
              } else {
                message = `This ${field} is already in use.`;
              }
            }

            fieldErrors[field as keyof typeof validationErrors] = message;
          }
        });

        if (Object.keys(fieldErrors).length > 0) {
          setValidationErrors(fieldErrors);

          // Show specific guidance for email conflicts
          if (fieldErrors.email?.includes("already registered")) {
            enqueueSnackbar("Email already registered. Please sign in instead.", { variant: "warning" });
          } else {
            const firstError = Object.values(fieldErrors)[0];
            enqueueSnackbar(firstError || "Validation error", { variant: "error" });
          }
        } else {
          enqueueSnackbar(err.message || "Sign up failed.", { variant: "error" });
        }
      } else {
        enqueueSnackbar(err.message || "Sign up failed. Please try again.", { variant: "error" });
      }
    }
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
    if (validationErrors.name) {
      setValidationErrors((prev) => ({ ...prev, name: undefined }));
    }
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
    if (validationErrors.username) {
      setValidationErrors((prev) => ({ ...prev, username: undefined }));
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (validationErrors.email) {
      setValidationErrors((prev) => ({ ...prev, email: undefined }));
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (validationErrors.password) {
      setValidationErrors((prev) => ({ ...prev, password: undefined }));
    }
  };

  const handlePasswordConfirmChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPasswordConfirm(e.target.value);
    if (validationErrors.passwordConfirm) {
      setValidationErrors((prev) => ({ ...prev, passwordConfirm: undefined }));
    }
  };

  return (
    <div className="w-full flex flex-col signup-page">
      {/* <Link to="/" className="w-fit">
        <Button className="m-8">Close</Button>
      </Link> */}
      <div className="flex flex-col lg:flex-row justify-between">
        <img src={signup} className="lg:w-1/2" />
        <div className="flex flex-col justify-center py-6 lg:py-0 px-5 lg:px-12 bg-white lg:w-1/2">
          <h1 className="font-bold text-[24px] lg:text-[64px]">
            Begin Reading
          </h1>
          <h2 className="font-bold text-[24px]">
            Kick-start your reading journey
          </h2>
          <form
            onSubmit={handleSubmit}
            className="w-full mt-4 lg:mt-12 flex flex-col gap-y-4"
          >
            <Input
              type="text"
              label="Name"
              value={name}
              onChange={handleNameChange}
              error={validationErrors.name}
            />
            <Input
              type="text"
              label="Username"
              value={username}
              onChange={handleUsernameChange}
              error={validationErrors.username}
            />
            <DatePicker
              label="Date of Birth"
              value={birthdate}
              onChange={(date) => {
                setBirthdate(date);
                if (validationErrors.birthdate) {
                  setValidationErrors((prev) => ({
                    ...prev,
                    birthdate: undefined,
                  }));
                }
              }}
              error={validationErrors.birthdate}
              maxDate={new Date()}
              minDate={new Date(1900, 0, 1)}
              placeholder="Select your birthdate"
            />
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-neutral-700">Choose Your Avatar</label>
              <div className="flex gap-4 justify-center">
                <img
                  src="/assets/default-avatar-m.png"
                  alt="Male avatar"
                  className={`cursor-pointer w-32 h-32 rounded-full transition ${
                    gender === 'male'
                      ? 'ring-4 ring-primary'
                      : 'opacity-60 hover:opacity-100'
                  }`}
                  onClick={() => {
                    setGender('male');
                    if (validationErrors.gender) {
                      setValidationErrors((prev) => ({ ...prev, gender: undefined }));
                    }
                  }}
                />
                <img
                  src="/assets/default-avatar-f.png"
                  alt="Female avatar"
                  className={`cursor-pointer w-32 h-32 rounded-full transition ${
                    gender === 'female'
                      ? 'ring-4 ring-primary'
                      : 'opacity-60 hover:opacity-100'
                  }`}
                  onClick={() => {
                    setGender('female');
                    if (validationErrors.gender) {
                      setValidationErrors((prev) => ({ ...prev, gender: undefined }));
                    }
                  }}
                />
              </div>
              {validationErrors.gender && (
                <p className="text-sm text-error">{validationErrors.gender}</p>
              )}
            </div>
            <Input
              type="email"
              label="Email"
              value={email}
              onChange={handleEmailChange}
              error={validationErrors.email}
            />
            <Input
              type="password"
              label="Password"
              value={password}
              onChange={handlePasswordChange}
              error={validationErrors.password}
            />
            <Input
              type="password"
              label="Confirm Password"
              value={passwordConfirm}
              onChange={handlePasswordConfirmChange}
              error={validationErrors.passwordConfirm}
            />
            <Button className="dark-blue-button" disabled={isSigningUp}>
              {isSigningUp ? "Creating Account..." : "Sign Up"}
            </Button>

            <div className="flex items-center gap-4 my-4">
              <div className="flex-1 h-px bg-gray-300"></div>
              <span className="text-gray-500 text-sm">OR</span>
              <div className="flex-1 h-px bg-gray-300"></div>
            </div>

            <p className="text-gray-700 text-sm text-center font-semibold">Google accounts require age 13+</p>

            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={isSigningInWithGoogle}
              className="flex items-center justify-center gap-3 w-full py-3 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              {isSigningInWithGoogle ? "Signing in..." : "Continue with Google"}
            </button>

            <Link
              to="/sign-in"
              className="text-action-500 hover:text-action-600 underline"
            >
              You have an account? Login
            </Link>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SignUpPage;
